/**
 * ModuleHelpButton — Bouton "?" contextuel à placer dans le header de chaque module.
 *
 * Ouvre un drawer latéral expliquant :
 *  - À quoi sert ce module
 *  - Comment il fonctionne (logique métier, sources de données)
 *  - Détail de chaque onglet
 *
 * Utilisation :
 *   <ModuleHelpButton moduleKey="origination" />
 *
 * Le contenu est centralisé dans MODULE_HELP_CONTENT (registry FR/EN/DE).
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { HelpCircle, X, BookOpen, Layers, Wrench } from "lucide-react";
import { MODULE_HELP_CONTENT } from "./moduleHelpContent";

export default function ModuleHelpButton({ moduleKey, size = "default" }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const [open, setOpen] = useState(false);

  const content = (MODULE_HELP_CONTENT[moduleKey] || {})[lang]
    || (MODULE_HELP_CONTENT[moduleKey] || {}).fr;

  if (!content) {
    return null;
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`btn-ghost ${size === "sm" ? "text-[10px] p-1.5" : "text-xs"} text-zinc-400 hover:text-[#378ADD]`}
        title={t("common.help", { defaultValue: "Aide" })}
        data-testid={`module-help-btn-${moduleKey}`}
      >
        <HelpCircle className={size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5"} />
        {size !== "sm" && <span>{t("common.help", { defaultValue: "Aide" })}</span>}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex justify-end"
          onClick={() => setOpen(false)}
          data-testid={`module-help-drawer-${moduleKey}`}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-md h-full bg-[#0d0d0e] border-l border-white/10 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 bg-[#0d0d0e]/95 backdrop-blur border-b border-white/10 px-5 py-4 flex items-center justify-between">
              <div>
                <div className="noam-overline inline-flex items-center gap-1.5">
                  <BookOpen className="w-3 h-3 text-[#378ADD]" />
                  {t("common.module_help", { defaultValue: "Aide module" })}
                </div>
                <div className="text-base font-semibold text-white mt-0.5">{content.title}</div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="btn-ghost p-1.5"
                data-testid={`module-help-close-${moduleKey}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-5 text-sm text-zinc-200">
              {/* À quoi sert ce module */}
              <div>
                <div className="noam-overline mb-2">
                  {t("common.purpose", { defaultValue: "À quoi sert ce module" })}
                </div>
                <p className="leading-relaxed">{content.purpose}</p>
              </div>

              {/* Comment ça marche */}
              {content.how_it_works && (
                <div>
                  <div className="noam-overline mb-2 inline-flex items-center gap-1.5">
                    <Wrench className="w-3 h-3 text-[#EF9F27]" />
                    {t("common.how_it_works", { defaultValue: "Comment ça marche" })}
                  </div>
                  <p className="leading-relaxed text-zinc-300">{content.how_it_works}</p>
                </div>
              )}

              {/* Onglets */}
              {(content.tabs || []).length > 0 && (
                <div>
                  <div className="noam-overline mb-2 inline-flex items-center gap-1.5">
                    <Layers className="w-3 h-3 text-[#7F77DD]" />
                    {t("common.tabs_explanation", { defaultValue: "Les onglets" })}
                  </div>
                  <div className="space-y-2.5">
                    {content.tabs.map((tab, i) => (
                      <div
                        key={i}
                        className="bg-white/[0.03] border border-white/5 rounded-lg p-3"
                      >
                        <div className="text-[13px] font-medium text-white mb-1">{tab.name}</div>
                        <div className="text-xs text-zinc-400 leading-relaxed">{tab.description}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions clés */}
              {(content.key_actions || []).length > 0 && (
                <div>
                  <div className="noam-overline mb-2">
                    {t("common.key_actions", { defaultValue: "Actions clés" })}
                  </div>
                  <ul className="space-y-1.5 text-xs text-zinc-300 list-disc list-inside">
                    {content.key_actions.map((a, i) => (
                      <li key={i} className="leading-relaxed">{a}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Modules liés */}
              {(content.related_modules || []).length > 0 && (
                <div>
                  <div className="noam-overline mb-2">
                    {t("common.related_modules", { defaultValue: "Modules connectés" })}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {content.related_modules.map((m, i) => (
                      <span
                        key={i}
                        className="text-[10px] px-2 py-0.5 rounded-full bg-[#378ADD]/10 text-[#378ADD] border border-[#378ADD]/20"
                      >
                        {m}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="text-[10px] text-zinc-600 pt-4 border-t border-white/5">
                {t("common.help_footer", { defaultValue: "Cette aide est disponible dans chaque module via le bouton ?" })}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
